/**
 * Tabuleiros.
 *
 * Cada jogo desenha a partir do estado serializado que o motor manda, e so
 * oferece o que esta em `legalMoves`. A interface nunca decide o que e legal:
 * ela desenha o que o arbitro disse que pode.
 */
import { useMemo, useState } from 'react';

import { Chess } from 'chess.js';
import { checkers, indexToSquare, squareToIndex } from '@engine/games/checkers.ts';
import { chess as chessRules, chessFen } from '@engine/games/chess.ts';
import type { Move } from '@engine/games/types.ts';

import type { GameType } from '../lib/adapter.ts';

interface BoardProps {
  readonly gameType: GameType;
  readonly state: string;
  readonly legalMoves: readonly Move[];
  readonly myTurn: boolean;
  readonly lastMove: Move | null;
  readonly onPlay: (move: Move) => void;
}

// ---------------------------------------------------------------------
// Jogo da velha
// ---------------------------------------------------------------------
function TicTacToe({ state, legalMoves, myTurn, onPlay }: Omit<BoardProps, 'gameType' | 'lastMove'>) {
  const casas = state.split('|')[0] ?? '---------';
  const disponiveis = new Set(legalMoves);

  return (
    // `grid-rows-3` não é enfeite: sem ele as linhas ficam com altura
    // automática, e a que recebe um ✕ ou ◯ cresce para caber o símbolo —
    // o tabuleiro mudava de forma a cada jogada. O 8x8 abaixo sempre
    // declarou `grid-rows-8`; aqui tinha ficado de fora.
    //
    // As duas classes do Tailwind viram `repeat(n, minmax(0, 1fr))`. O
    // `minmax(0, ...)` é o que impede o conteúdo de empurrar a faixa:
    // `1fr` sozinho tem mínimo automático e voltaria a esticar.
    <div className="moldura grid grid-cols-3 grid-rows-3 gap-2 aspect-square w-full max-w-[min(100%,62vh,30rem)] mx-auto">
      {[...casas].map((celula, index) => {
        const jogavel = myTurn && disponiveis.has(String(index));
        return (
          <button
            key={index}
            type="button"
            disabled={!jogavel}
            onClick={() => onPlay(String(index))}
            aria-label={`casa ${index + 1}${celula === '-' ? ' vazia' : ''}`}
            className={[
              'rounded-lg grid place-items-center font-bold transition',
              'text-[clamp(2rem,9vw,3.5rem)] leading-none',
              jogavel ? 'cursor-pointer hover:brightness-110' : 'cursor-default',
            ].join(' ')}
            style={{
              background: 'var(--board-dark)',
              color: celula === '0' ? 'var(--piece-light)' : 'var(--board-highlight)',
              boxShadow: 'inset 0 2px 6px oklch(0 0 0 / 0.25)',
            }}
          >
            <span className={celula === '-' ? '' : 'peca-entra'}>
              {celula === '0' ? '✕' : celula === '1' ? '◯' : ''}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------
// Grade 8x8 compartilhada por damas e xadrez
// ---------------------------------------------------------------------
interface SquareProps {
  readonly row: number;
  readonly col: number;
  /** Mostra a letra da coluna (ultima linha) e o numero da linha (coluna a). */
  readonly coordenadas?: boolean;
  readonly selected: boolean;
  readonly target: boolean;
  readonly highlighted: boolean;
  readonly onClick: () => void;
  readonly disabled: boolean;
  readonly label: string;
  readonly children?: React.ReactNode;
}

const LETRAS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;

function Square({
  row,
  col,
  coordenadas,
  selected,
  target,
  highlighted,
  onClick,
  disabled,
  label,
  children,
}: SquareProps) {
  const escura = (row + col) % 2 === 1;
  // A marca usa a cor da casa oposta, para ler em qualquer uma das duas.
  const corMarca = escura ? 'var(--board-light)' : 'var(--board-dark)';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={[
        'relative grid place-items-center transition',
        escura ? 'bg-board-dark' : 'bg-board-light',
        disabled ? 'cursor-default' : 'cursor-pointer',
      ].join(' ')}
      style={selected ? { boxShadow: 'inset 0 0 0 4px var(--board-highlight)' } : undefined}
    >
      {highlighted && !selected ? (
        <span className="absolute inset-0 bg-board-highlight/35" aria-hidden="true" />
      ) : null}
      {target ? (
        <span
          className="absolute size-1/3 rounded-full bg-board-highlight/80"
          aria-hidden="true"
        />
      ) : null}
      {/* O wrapper precisa ocupar a casa inteira: tamanhos em porcentagem
          dentro dele (a pedra das damas) resolvem contra esta caixa. */}
      {coordenadas && col === 0 ? (
        <span style={{ color: corMarca }}>
          <Coordenada texto={String(8 - row)} canto="esquerdo" />
        </span>
      ) : null}
      {coordenadas && row === 7 ? (
        <span style={{ color: corMarca }}>
          <Coordenada texto={LETRAS[col] ?? ''} canto="inferior" />
        </span>
      ) : null}

      <span className="relative grid place-items-center size-full">{children}</span>
    </button>
  );
}

/**
 * Moldura e grade. As coordenadas ficam dentro das casas da borda, como
 * nos sites de xadrez: fora do tabuleiro elas exigiriam uma calha so de um
 * lado e deixariam a moldura torta.
 */
function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-full max-w-[min(100%,74vh,40rem)] mx-auto moldura">
      <div className="aspect-square grid grid-cols-8 grid-rows-8 overflow-hidden rounded-sm">
        {children}
      </div>
    </div>
  );
}

/** Marca de coordenada no canto da casa. Minuscula, como manda a notacao. */
function Coordenada({ texto, canto }: { texto: string; canto: 'inferior' | 'esquerdo' }) {
  return (
    <span
      aria-hidden="true"
      className="absolute text-[0.5625rem] font-bold leading-none opacity-70 pointer-events-none"
      style={
        canto === 'inferior'
          ? { right: '0.15rem', bottom: '0.1rem' }
          : { left: '0.15rem', top: '0.1rem' }
      }
    >
      {texto}
    </span>
  );
}

// ---------------------------------------------------------------------
// Damas
// ---------------------------------------------------------------------
function Checkers({ state, legalMoves, myTurn, lastMove, onPlay }: Omit<BoardProps, 'gameType'>) {
  // `passos` guarda a sequencia ja escolhida numa captura multipla.
  const [passos, setPassos] = useState<number[]>([]);
  const posicao = useMemo(() => checkers.deserialize(state), [state]);

  // Candidatos compativeis com o que ja foi clicado.
  const candidatos = useMemo(() => {
    if (passos.length === 0) return legalMoves;
    const prefixo = passos.map((i) => indexToSquare(i));
    return legalMoves.filter((move) => {
      const casas = move.split(/[-x]/).map(Number);
      return prefixo.every((casa, i) => casas[i] === casa);
    });
  }, [legalMoves, passos]);

  const origens = useMemo(
    () => new Set(legalMoves.map((m) => squareToIndex(Number(m.split(/[-x]/)[0]))))
    , [legalMoves],
  );

  const destinos = useMemo(() => {
    if (passos.length === 0) return new Set<number>();
    const nivel = passos.length;
    const saida = new Set<number>();
    for (const move of candidatos) {
      const casas = move.split(/[-x]/).map(Number);
      const proxima = casas[nivel];
      if (proxima !== undefined) saida.add(squareToIndex(proxima));
    }
    return saida;
  }, [candidatos, passos]);

  const ultimas = useMemo(() => {
    if (!lastMove) return new Set<number>();
    return new Set(lastMove.split(/[-x]/).map((c) => squareToIndex(Number(c))));
  }, [lastMove]);

  function clicar(index: number): void {
    if (!myTurn) return;

    if (passos.length === 0) {
      if (origens.has(index)) setPassos([index]);
      return;
    }

    if (index === passos[0] && passos.length === 1) {
      setPassos([]);
      return;
    }

    if (!destinos.has(index)) return;

    const novo = [...passos, index];
    const alvo = novo.map((i) => indexToSquare(i));
    const completo = candidatos.find((move) => {
      const casas = move.split(/[-x]/).map(Number);
      return casas.length === alvo.length && alvo.every((casa, i) => casas[i] === casa);
    });

    if (completo) {
      onPlay(completo);
      setPassos([]);
      return;
    }
    // Ainda no meio de uma sequencia de captura.
    setPassos(novo);
  }

  return (
    <Grid>
      {Array.from({ length: 64 }, (_unused, index) => {
        const row = Math.floor(index / 8);
        const col = index % 8;
        const peca = posicao.board[index];
        const jogavel = myTurn && (origens.has(index) || destinos.has(index));

        return (
          <Square
            key={index}
            row={row}
            col={col}
            selected={passos.includes(index)}
            target={destinos.has(index)}
            highlighted={ultimas.has(index)}
            disabled={!jogavel}
            onClick={() => clicar(index)}
            label={`casa ${(row + col) % 2 === 1 ? indexToSquare(index) : ''}`}
          >
            {peca ? (
              <span
                className={[
                  'peca-entra block w-[74%] aspect-square rounded-full grid place-items-center',
                  'text-[clamp(0.75rem,2.2vw,1.25rem)] leading-none',
                ].join(' ')}
                style={{
                  // Gradiente radial deslocado: a peca ganha volume em vez
                  // de parecer um circulo chapado.
                  background:
                    peca.seat === 0
                      ? 'radial-gradient(circle at 34% 28%, color-mix(in oklab, var(--piece-light) 100%, white 12%), var(--piece-light) 55%, color-mix(in oklab, var(--piece-light) 82%, black 18%))'
                      : 'radial-gradient(circle at 34% 28%, color-mix(in oklab, var(--piece-dark) 78%, white 22%), var(--piece-dark) 58%, color-mix(in oklab, var(--piece-dark) 88%, black 12%))',
                  boxShadow:
                    '0 2px 3px oklch(0 0 0 / 0.35), inset 0 -2px 3px oklch(0 0 0 / 0.22), inset 0 2px 2px oklch(1 0 0 / 0.25)',
                  color: peca.seat === 0 ? 'var(--piece-dark)' : 'var(--piece-light)',
                }}
              >
                {peca.king ? '♛' : ''}
              </span>
            ) : null}
          </Square>
        );
      })}
    </Grid>
  );
}

// ---------------------------------------------------------------------
// Xadrez
// ---------------------------------------------------------------------
const GLIFOS: Record<string, string> = {
  wk: '♔', wq: '♕', wr: '♖', wb: '♗', wn: '♘', wp: '♙',
  bk: '♚', bq: '♛', br: '♜', bb: '♝', bn: '♞', bp: '♟',
};

const COLUNAS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;

function nomeCasa(row: number, col: number): string {
  return `${COLUNAS[col]}${8 - row}`;
}

function ChessBoard({ state, legalMoves, myTurn, lastMove, onPlay }: Omit<BoardProps, 'gameType'>) {
  const [origem, setOrigem] = useState<string | null>(null);

  const { tabuleiro, verbosos } = useMemo(() => {
    const fen = chessFen(chessRules.deserialize(state));
    const jogo = new Chess(fen);
    return { tabuleiro: jogo.board(), verbosos: jogo.moves({ verbose: true }) };
  }, [state]);

  const legais = useMemo(() => new Set(legalMoves), [legalMoves]);
  // Só os lances que o árbitro confirmou como legais nesta posição.
  const disponiveis = useMemo(
    () => verbosos.filter((m) => legais.has(m.san)),
    [verbosos, legais],
  );

  // Sets de string: os tipos da chess.js usam um union de casas, e comparar
  // com a casa clicada (string comum) exige alargar aqui.
  const origens = useMemo(
    () => new Set<string>(disponiveis.map((m) => m.from)),
    [disponiveis],
  );
  const destinos = useMemo(
    () => new Set<string>(disponiveis.filter((m) => m.from === origem).map((m) => m.to)),
    [disponiveis, origem],
  );

  const ultimas = useMemo(() => {
    if (!lastMove) return new Set<string>();
    const encontrado = verbosos.find((m) => m.san === lastMove);
    return new Set(encontrado ? [encontrado.from, encontrado.to] : []);
  }, [lastMove, verbosos]);

  function clicar(casa: string): void {
    if (!myTurn) return;

    if (origem === casa) {
      setOrigem(null);
      return;
    }

    if (destinos.has(casa)) {
      const candidatos = disponiveis.filter((m) => m.from === origem && m.to === casa);
      // Promocao: a dama e a escolha de mais de 95% das partidas; oferecer
      // as outras pecas exige uma caixa de dialogo que ainda nao existe.
      const escolhido = candidatos.find((m) => m.promotion === 'q') ?? candidatos[0];
      if (escolhido) onPlay(escolhido.san);
      setOrigem(null);
      return;
    }

    if (origens.has(casa)) setOrigem(casa);
    else setOrigem(null);
  }

  return (
    <Grid>
      {tabuleiro.flatMap((linha, row) =>
        linha.map((peca, col) => {
          const casa = nomeCasa(row, col);
          const jogavel = myTurn && (origens.has(casa) || destinos.has(casa));
          return (
            <Square
              key={casa}
              row={row}
              col={col}
              coordenadas
              selected={origem === casa}
              target={destinos.has(casa)}
              highlighted={ultimas.has(casa)}
              disabled={!jogavel}
              onClick={() => clicar(casa)}
              label={`casa ${casa}`}
            >
              {peca ? (
                <span
                  className="peca-entra text-[clamp(1.5rem,5vw,2.75rem)] leading-none"
                  style={{
                    color: peca.color === 'w' ? 'var(--piece-light)' : 'var(--piece-dark)',
                    // Contorno na peca, nao so sombra: o glifo branco sobre
                    // casa clara sumia, e e justamente metade do tabuleiro.
                    WebkitTextStroke:
                      peca.color === 'w'
                        ? '1.5px color-mix(in oklab, var(--piece-dark) 85%, transparent)'
                        : '1px color-mix(in oklab, var(--piece-light) 55%, transparent)',
                    paintOrder: 'stroke fill',
                    textShadow: '0 2px 3px oklch(0 0 0 / 0.35)',
                  }}
                >
                  {GLIFOS[`${peca.color}${peca.type}`]}
                </span>
              ) : null}
            </Square>
          );
        }),
      )}
    </Grid>
  );
}

// ---------------------------------------------------------------------
export function Board(props: BoardProps) {
  if (props.gameType === 'TIC_TAC_TOE') {
    return (
      <TicTacToe
        state={props.state}
        legalMoves={props.legalMoves}
        myTurn={props.myTurn}
        onPlay={props.onPlay}
      />
    );
  }
  if (props.gameType === 'CHECKERS') return <Checkers {...props} />;
  return <ChessBoard {...props} />;
}
